import { Injectable, signal, computed } from '@angular/core';

export interface CarrinhoItem {
  medicamentoId: number;
  nome: string;
  preco: number;
  quantidade: number;
  farmaciaId: number;
  farmaciaNome?: string;
  imagem?: string;
}

export interface OpcoesEntrega {
  tipo: 'levantamento' | 'entrega';
  endereco?: string;
  taxaEntrega: number;
}

@Injectable({
  providedIn: 'root'
})
export class CarrinhoService {
  private items = signal<CarrinhoItem[]>([]);
  private opcoesEntrega = signal<OpcoesEntrega>({
    tipo: 'levantamento',
    taxaEntrega: 0
  });
  
  carrinho = computed(() => this.items());
  entrega = computed(() => this.opcoesEntrega());
  
  subtotal = computed(() => 
    this.items().reduce((sum, item) => sum + (item.preco * item.quantidade), 0)
  );
  
  total = computed(() => 
    this.subtotal() + this.opcoesEntrega().taxaEntrega
  );
  
  quantidade = computed(() => 
    this.items().reduce((sum, item) => sum + item.quantidade, 0)
  );

  // Taxa de entrega padrão: 500 AOA
  readonly TAXA_ENTREGA_PADRAO = 500;

  constructor() {
    this.loadFromStorage();
  }

  addItem(item: CarrinhoItem): void {
    const current = this.items();
    const existingIndex = current.findIndex(i => 
      i.medicamentoId === item.medicamentoId && i.farmaciaId === item.farmaciaId
    );

    if (existingIndex >= 0) {
      current[existingIndex].quantidade += item.quantidade;
      this.items.set([...current]);
    } else {
      this.items.set([...current, item]);
    }

    this.saveToStorage();
  }

  removeItem(medicamentoId: number, farmaciaId: number): void {
    this.items.set(this.items().filter(item => 
      !(item.medicamentoId === medicamentoId && item.farmaciaId === farmaciaId)
    ));
    this.saveToStorage();
  }

  updateQuantidade(medicamentoId: number, farmaciaId: number, quantidade: number): void {
    const current = this.items();
    const index = current.findIndex(i => 
      i.medicamentoId === medicamentoId && i.farmaciaId === farmaciaId
    );
    
    if (index >= 0) {
      if (quantidade <= 0) {
        this.removeItem(medicamentoId, farmaciaId);
      } else {
        current[index].quantidade = quantidade;
        this.items.set([...current]);
        this.saveToStorage();
      }
    }
  }

  setOpcoesEntrega(opcoes: OpcoesEntrega): void {
    this.opcoesEntrega.set(opcoes);
    this.saveToStorage();
  }

  setTipoEntrega(tipo: 'levantamento' | 'entrega', endereco?: string): void {
    const taxaEntrega = tipo === 'entrega' ? this.TAXA_ENTREGA_PADRAO : 0;
    this.opcoesEntrega.set({
      tipo,
      endereco,
      taxaEntrega
    });
    this.saveToStorage();
  }

  clear(): void {
    this.items.set([]);
    this.opcoesEntrega.set({
      tipo: 'levantamento',
      taxaEntrega: 0
    });
    this.saveToStorage();
  }

  private saveToStorage(): void {
    localStorage.setItem('carrinho', JSON.stringify(this.items()));
    localStorage.setItem('carrinho_entrega', JSON.stringify(this.opcoesEntrega()));
  }

  private loadFromStorage(): void {
    const saved = localStorage.getItem('carrinho');
    if (saved) {
      this.items.set(JSON.parse(saved));
    }
    
    const savedEntrega = localStorage.getItem('carrinho_entrega');
    if (savedEntrega) {
      this.opcoesEntrega.set(JSON.parse(savedEntrega));
    }
  }
}
